import React, { useMemo } from "react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import styles from "./CalendaCalendar.module.css";
import AppointmentCard from "./AppointmentCard";
import { computeEventColumns } from "./computeEventColumns";
import {
  HOUR_HEIGHT_PX,
  dayKeyBRT,
  getProfessionalColor,
  getBRTParts,
} from "./calendarHelpers";

const SLOT_HEIGHT_PX = HOUR_HEIGHT_PX / 4;

/**
 * Linha horizontal vermelha indicando o horário atual (BRT). Renderizada
 * apenas quando "agora" cai dentro da faixa visível e na coluna do dia de hoje.
 */
const NowLine = ({ nowMinutes, gridMinutes }) => {
  if (nowMinutes < 0 || nowMinutes > gridMinutes) return null;
  const top = (nowMinutes / 60) * HOUR_HEIGHT_PX;
  return (
    <div className={styles.nowLine} style={{ top: `${top}px` }} aria-hidden="true">
      <span className={styles.nowDot} />
    </div>
  );
};

/**
 * Uma coluna de dia: slots clicáveis + cards posicionados via overlap.
 */
const DayColumn = ({
  day,
  appointments,
  professionalColors,
  statusOptions,
  isBlocked,
  isToday,
  nowMinutes,
  gridStartHour,
  gridMinutes,
  gridHeightPx,
  slots,
  onSlotClick,
  onAppointmentClick,
  onBlockedLink,
}) => {
  const dayKey = format(day, "yyyy-MM-dd");

  const dayAppointments = useMemo(
    () => appointments.filter((app) => dayKeyBRT(app.startTime) === dayKey),
    [appointments, dayKey],
  );

  const layout = useMemo(
    () => computeEventColumns(dayAppointments),
    [dayAppointments],
  );

  return (
    <div
      className={`${styles.dayColumn}${isBlocked ? ` ${styles.dayColumnBlocked}` : ""}`}
    >
      <div className={`${styles.dayHeader}${isToday ? ` ${styles.dayHeaderToday}` : ""}`}>
        <span className={styles.dayName}>
          {format(day, "eee", { locale: ptBR })}
        </span>
        <span className={styles.dayNumber}>{format(day, "d")}</span>
      </div>

      {isBlocked && (
        <div className={styles.blockedBanner}>
          <span className={styles.blockedText}>🚫 BLOQUEADO</span>
          <button
            className={styles.blockedLink}
            onClick={(e) => {
              e.stopPropagation();
              onBlockedLink();
            }}
          >
            Ver bloqueios
          </button>
        </div>
      )}

      <div className={styles.slots} style={{ height: `${gridHeightPx}px` }}>
        {slots.map(({ hour, minute }) => {
          const timeStr = `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
          return (
            <div
              key={timeStr}
              className={styles.slot}
              style={{ height: `${SLOT_HEIGHT_PX}px` }}
              onClick={() => onSlotClick({ day, time: timeStr })}
            >
              <span className={styles.addIcon}>+</span>
            </div>
          );
        })}

        {isToday && <NowLine nowMinutes={nowMinutes} gridMinutes={gridMinutes} />}

        {layout.map(({ event, column, colSpan, totalColumns }) => (
          <AppointmentCard
            key={event.id}
            appointment={event}
            column={column}
            colSpan={colSpan}
            totalColumns={totalColumns}
            statusOptions={statusOptions}
            gridStartHour={gridStartHour}
            gridMinutes={gridMinutes}
            color={
              professionalColors[event.professionalId] ??
              getProfessionalColor(event.professionalId)
            }
            onClick={onAppointmentClick}
          />
        ))}
      </div>
    </div>
  );
};

/**
 * Grade de tempo (sem tabela HTML). Renderiza a coluna de horários + N colunas
 * de dia (1 no modo "dia", 6 no modo "semana"). Eventos posicionados em
 * absolute com layout de colunas para overlap.
 *
 * A faixa horária (`gridStartHour`/`gridEndHour`) é dinâmica: derivada de
 * businessHours ∪ agendamentos pelo orquestrador (CalendarCalendar) e passada
 * aqui. Toda a geometria (labels, slots, altura, linha-"agora") é relativa a
 * essa faixa.
 */
const CalendarGrid = ({
  view,
  days,
  appointments,
  professionalColors,
  statusOptions,
  blockedDates,
  gridStartHour,
  gridEndHour,
  onSlotClick,
  onAppointmentClick,
  onBlockedLink,
}) => {
  const gridMinutes = (gridEndHour - gridStartHour) * 60;
  const gridHeightPx = (gridMinutes / 60) * HOUR_HEIGHT_PX;

  // Labels de hora da coluna lateral (gridStartHour … gridEndHour inclusivo).
  const hourLabels = useMemo(() => {
    const labels = [];
    for (let h = gridStartHour; h <= gridEndHour; h++) labels.push(h);
    return labels;
  }, [gridStartHour, gridEndHour]);

  // Slots de 15min para captura de clique (mantém o "+" esperado pelos testes).
  const slots = useMemo(() => {
    const out = [];
    for (let h = gridStartHour; h < gridEndHour; h++) {
      for (let m = 0; m < 60; m += 15) out.push({ hour: h, minute: m });
    }
    return out;
  }, [gridStartHour, gridEndHour]);

  // "Agora" em BRT, em minutos desde o início da faixa.
  const nowMinutes = useMemo(() => {
    const { hour, minute } = getBRTParts(new Date());
    return (hour - gridStartHour) * 60 + minute;
  }, [gridStartHour]);

  const todayKey = useMemo(() => dayKeyBRT(new Date()), []);

  return (
    <div
      className={`${styles.grid} ${view === "day" ? styles.gridDay : styles.gridWeek}`}
    >
      <div className={styles.timeColumn}>
        <div className={styles.spacer}></div>
        <div
          className={styles.timeLabelsContainer}
          style={{ height: `${gridHeightPx}px` }}
        >
          {hourLabels.map((h) => (
            <div
              key={h}
              className={styles.timeLabel}
              style={{ top: `${(h - gridStartHour) * HOUR_HEIGHT_PX}px` }}
            >
              {String(h).padStart(2, "0")}:00
            </div>
          ))}
        </div>
      </div>

      {days.map((day) => {
        const calendarDayStr = format(day, "yyyy-MM-dd");
        return (
          <DayColumn
            key={day.toString()}
            day={day}
            appointments={appointments}
            professionalColors={professionalColors}
            statusOptions={statusOptions}
            isBlocked={blockedDates.includes(calendarDayStr)}
            isToday={calendarDayStr === todayKey}
            nowMinutes={nowMinutes}
            gridStartHour={gridStartHour}
            gridMinutes={gridMinutes}
            gridHeightPx={gridHeightPx}
            slots={slots}
            onSlotClick={onSlotClick}
            onAppointmentClick={onAppointmentClick}
            onBlockedLink={onBlockedLink}
          />
        );
      })}
    </div>
  );
};

export default CalendarGrid;
